import { computed, readonly, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';
import type { SearchFilterPayload } from '@/composables/api/SearchApi';
import type { SortDirection } from '@/types/tableTypes';

/**
 * How the rows are chosen.
 *
 * - `ids`: the user ticked rows one by one. Only those ids are selected.
 * - `search`: the user chose "Select all N matching". Every match of the search is selected,
 *   except the rows the user unticked afterwards.
 */
export type BulkSelectionMode = 'ids' | 'search';

/**
 * The `mode` a "select all matching" selection sends.
 *
 * - `search`: every match of the entity's own search. Every bulk-selectable entity uses it.
 * - `list_search`: the schedules that pass a custom list search. The custom list page's
 *   schedule selection uses it, because the page searches lists, not schedules. Only the
 *   schedule entity accepts it.
 */
export type BulkSelectAllMode = 'search' | 'list_search';

/**
 * The `selection` node of a bulk-action request, exactly as the API contract describes it.
 *
 * `mode` is always present, so an empty `ids` array can never be read as "everything".
 */
export type BulkSelectionPayload =
  | { mode: 'ids'; ids: number[] }
  | {
      mode: BulkSelectAllMode;
      /** What the search request posts: `buildSearchFilterPayload(compositeFilter)`. Null means no filter. */
      filter: SearchFilterPayload | null;
      /** The page's `columnSort`. The server uses it only to order the report. */
      sort: Record<string, SortDirection>;
      /** The rows unticked after "Select all". */
      excluded_ids: number[];
      /**
       * The total the page showed, before `excluded_ids` are taken off. For `list_search` it is
       * the matching schedule count the bulk bar showed.
       */
      expected_count: number;
    };

/** The search on screen, as the page already builds it for its search request. */
export interface BulkSelectionSearchInputs {
  filter?: SearchFilterPayload | null;
  sort?: Record<string, SortDirection>;
}

export interface UseBulkSelectionOptions {
  /** The ids of the rows on the current page, in the order they are shown. */
  pageRowIds: MaybeRefOrGetter<readonly number[]>;
  /** The pager's cached total for the search on screen. */
  total: MaybeRefOrGetter<number>;
  /** The filter and sort the page searched with. Read when the payload is built. */
  searchInputs: MaybeRefOrGetter<BulkSelectionSearchInputs | null | undefined>;
  /**
   * Any value that changes when the entity, the filter or the sort changes.
   * The selection is cleared whenever it changes, the same way the page resets its pager.
   */
  resetKey: MaybeRefOrGetter<unknown>;
  /** The `mode` a "select all matching" payload sends. Defaults to `search`. */
  selectAllMode?: BulkSelectAllMode;
}

/** Only the modifier the range logic reads. A MouseEvent or KeyboardEvent both fit. */
type SelectionGesture = Pick<MouseEvent, 'shiftKey'>;

/** A row id as the API wants it: a whole number. Anything else is not selectable. */
export function toBulkSelectionId(value: unknown): number | null {
  const id = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isInteger(id) ? id : null;
}

/**
 * The page's one selection of rows: the row the pane shows, and the rows a bulk action covers.
 *
 * A plain click selects one row alone. Cmd-click adds or removes a row, Shift-click selects the
 * loaded rows from the anchor to the clicked row, and Cmd-A selects every loaded row. On a narrow
 * screen a long press selects a row. Custom list cards still tick rows with their checkboxes.
 * Once a row is selected, the user can widen the selection to every match of the search.
 * After that, removing a row adds it to a list of exclusions rather than to the chosen ids.
 *
 * Changing the entity, the filter or the sort clears everything. The rows on screen are then a
 * different result set, and a selection made against the old one would act on the wrong rows.
 */
export function useBulkSelection(options: UseBulkSelectionOptions) {
  const mode = ref<BulkSelectionMode>('ids');
  const chosenIds = ref<Set<number>>(new Set());
  const excludedIds = ref<Set<number>>(new Set());
  /**
   * The row a Shift-click extends from: the row last clicked, Cmd-clicked or ticked.
   * A Shift-click itself leaves it where it is.
   */
  const anchorId = ref<number | null>(null);
  /**
   * True when the user chose the rows on purpose: by Cmd-click, Shift-click, Cmd-A or a tick.
   * A plain click selects one row for the pane only, so it leaves this false and shows no bar.
   */
  const deliberate = ref(false);

  const pageRowIds = computed(() => toValue(options.pageRowIds));
  const total = computed(() => toValue(options.total) || 0);

  function isSelected(rawId: unknown): boolean {
    const id = toBulkSelectionId(rawId);
    if (id === null) return false;
    return mode.value === 'search' ? !excludedIds.value.has(id) : chosenIds.value.has(id);
  }

  /** How many rows a bulk action would cover right now. */
  const count = computed(() =>
    mode.value === 'search'
      ? Math.max(0, total.value - excludedIds.value.size)
      : chosenIds.value.size,
  );

  const hasSelection = computed(() => count.value > 0);

  /**
   * Whether "select all matching" would add rows, even when nothing is ticked yet.
   * The custom list page offers "112 schedules" as soon as one list is ticked, so it reads this.
   */
  const hasUntickedMatches = computed(() => mode.value === 'ids' && total.value > chosenIds.value.size);

  /**
   * Whether to offer "Select all N matching".
   * It shows as soon as one row is ticked, and only when the search has more matches than are
   * ticked. Otherwise the offer would select nothing new.
   */
  const canSelectAllMatching = computed(() => hasUntickedMatches.value && chosenIds.value.size > 0);

  /** The one selected row, when exactly one row is chosen by id. The pane shows this row. */
  const soleSelectedId = computed<number | null>(() => {
    if (mode.value !== 'ids' || chosenIds.value.size !== 1) return null;
    const [onlyId] = chosenIds.value;
    return onlyId;
  });

  /**
   * Whether the selection bar shows: two or more rows, every match, or one row chosen on purpose.
   * A single row from a plain click shows only the pane.
   */
  const showsBar = computed(
    () => count.value >= 2 || mode.value === 'search' || (count.value === 1 && deliberate.value),
  );

  /** Tick or untick one row, in whichever mode is active. */
  function setOne(id: number, selected: boolean, chosen: Set<number>, excluded: Set<number>) {
    if (mode.value === 'search') {
      if (selected) excluded.delete(id);
      else excluded.add(id);
      return;
    }
    if (selected) chosen.add(id);
    else chosen.delete(id);
  }

  /**
   * The loaded rows from the anchor to this row, in the order shown, both ends included.
   * Null when there is no anchor, or when the anchor or this row is not loaded.
   */
  function anchorRangeIds(id: number): number[] | null {
    const ids = pageRowIds.value;
    const anchorIndex = anchorId.value === null ? -1 : ids.indexOf(anchorId.value);
    const targetIndex = ids.indexOf(id);
    if (anchorIndex === -1 || targetIndex === -1) return null;

    const [low, high] = anchorIndex <= targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex];
    return ids.slice(low, high + 1);
  }

  /**
   * Toggle one row.
   *
   * With shift held and an anchor row on this page, every row between the anchor and this row
   * takes this row's new state. That matches how a file manager extends a selection.
   */
  function toggle(rawId: unknown, event?: SelectionGesture) {
    const id = toBulkSelectionId(rawId);
    if (id === null) return;

    const nextState = !isSelected(id);
    const chosen = new Set(chosenIds.value);
    const excluded = new Set(excludedIds.value);

    const rangeIds = event?.shiftKey ? anchorRangeIds(id) : null;
    for (const rangeId of rangeIds ?? [id]) {
      setOne(rangeId, nextState, chosen, excluded);
    }

    chosenIds.value = chosen;
    excludedIds.value = excluded;
    anchorId.value = id;
    deliberate.value = true;
  }

  /**
   * A plain click: this row alone, by id. It becomes the anchor, and the bar stays hidden.
   * `onPurpose` keeps the bar, as when ↓ moves a row that was selected on purpose.
   */
  function selectOnly(rawId: unknown, { onPurpose = false }: { onPurpose?: boolean } = {}) {
    const id = toBulkSelectionId(rawId);
    if (id === null) return;

    mode.value = 'ids';
    chosenIds.value = new Set([id]);
    excludedIds.value = new Set();
    anchorId.value = id;
    deliberate.value = onPurpose;
  }

  /** Cmd-click or Ctrl-click: flip one row and leave the rest. In search mode it excludes the row. */
  function toggleOne(rawId: unknown) {
    const id = toBulkSelectionId(rawId);
    if (id === null) return;

    const chosen = new Set(chosenIds.value);
    const excluded = new Set(excludedIds.value);
    setOne(id, !isSelected(id), chosen, excluded);

    chosenIds.value = chosen;
    excludedIds.value = excluded;
    anchorId.value = id;
    deliberate.value = true;
  }

  /**
   * A long press: add this row and leave the rest. Unlike a Cmd-click, it never removes a row,
   * so a long press on a row already selected keeps it selected.
   */
  function selectOne(rawId: unknown) {
    const id = toBulkSelectionId(rawId);
    if (id === null) return;

    const chosen = new Set(chosenIds.value);
    const excluded = new Set(excludedIds.value);
    setOne(id, true, chosen, excluded);

    chosenIds.value = chosen;
    excludedIds.value = excluded;
    anchorId.value = id;
    deliberate.value = true;
  }

  /**
   * Shift-click: select every loaded row from the anchor to this row, in the order shown.
   * Rows outside the range stay as they are, and the anchor does not move.
   * With no anchor, or an anchor that is no longer loaded, it acts as a Cmd-click.
   */
  function extendTo(rawId: unknown) {
    const id = toBulkSelectionId(rawId);
    if (id === null) return;

    const rangeIds = anchorRangeIds(id);
    if (rangeIds === null) {
      toggleOne(id);
      return;
    }

    const chosen = new Set(chosenIds.value);
    const excluded = new Set(excludedIds.value);
    for (const rangeId of rangeIds) {
      setOne(rangeId, true, chosen, excluded);
    }

    chosenIds.value = chosen;
    excludedIds.value = excluded;
    deliberate.value = true;
  }

  /** Cmd-A: add every loaded row. Rows chosen on earlier pages stay. */
  function selectAllLoaded() {
    const chosen = new Set(chosenIds.value);
    const excluded = new Set(excludedIds.value);
    for (const id of pageRowIds.value) {
      setOne(id, true, chosen, excluded);
    }

    chosenIds.value = chosen;
    excludedIds.value = excluded;
    deliberate.value = true;
  }

  /**
   * Drop every chosen row that is not in `rawIds`. The player list uses it to forget players that
   * left the loaded records. It only narrows an `ids` selection.
   */
  function retainOnly(rawIds: Iterable<unknown>) {
    if (mode.value !== 'ids') return;

    const keptIds = new Set<number>();
    for (const rawId of rawIds) {
      const id = toBulkSelectionId(rawId);
      if (id !== null) keptIds.add(id);
    }
    chosenIds.value = new Set([...chosenIds.value].filter((id) => keptIds.has(id)));
  }

  /** Widen the selection to every match of the search. Nothing is excluded yet. */
  function selectAllMatching() {
    mode.value = 'search';
    chosenIds.value = new Set();
    excludedIds.value = new Set();
  }

  function clear() {
    mode.value = 'ids';
    chosenIds.value = new Set();
    excludedIds.value = new Set();
    anchorId.value = null;
    deliberate.value = false;
  }

  /**
   * The `selection` node for a bulk-action request.
   * Null when nothing is selected, so a caller can never send an empty selection.
   */
  function buildSelectionPayload(): BulkSelectionPayload | null {
    if (!hasSelection.value) return null;

    if (mode.value === 'ids') {
      return { mode: 'ids', ids: [...chosenIds.value] };
    }

    const inputs = toValue(options.searchInputs);
    return {
      mode: options.selectAllMode ?? 'search',
      filter: inputs?.filter ?? null,
      sort: inputs?.sort ?? {},
      excluded_ids: [...excludedIds.value],
      expected_count: total.value,
    };
  }

  watch(
    () => toValue(options.resetKey),
    () => clear(),
  );

  return {
    mode: readonly(mode),
    chosenIds: readonly(chosenIds),
    excludedIds: readonly(excludedIds),
    deliberate: readonly(deliberate),
    count,
    hasSelection,
    soleSelectedId,
    showsBar,
    canSelectAllMatching,
    hasUntickedMatches,
    isSelected,
    toggle,
    selectOnly,
    toggleOne,
    selectOne,
    extendTo,
    selectAllLoaded,
    retainOnly,
    selectAllMatching,
    clear,
    buildSelectionPayload,
  };
}

export type BulkSelection = ReturnType<typeof useBulkSelection>;
